import { ReactNode, useEffect, useMemo, useState } from "react"

export default function ProgressBar_v2({
  complete,
  sections: customSections,
  activeSection,
  onNavigate
}: {
  complete: Record<string, boolean>
  sections?: { label: string; short: string; icon: ReactNode; target: string }[]
  activeSection?: string
  onNavigate?: (target: string) => void
}) {
  const defaultSections = useMemo(
    () => [
      { label: "Designs", short: "Designs", icon: "/bildmarke2.svg", target: "designs" },
      { label: "Applicant and Representative", short: "Applicant", icon: "/anmeldervertreter.svg", target: "applicant" },
      { label: "Signature", short: "Signature", icon: "/unterschrift.svg", target: "signature" },
      { label: "Payment", short: "Payment", icon: "/zahlungsverfahren.svg", target: "payment" }
    ],
    []
  )

  const sections = customSections || defaultSections

  const [hoveredSection, setHoveredSection] = useState<string | null>(null)

  useEffect(() => {
    const cleanups: Array<() => void> = []

    sections.forEach(({ target }) => {
      const element = document.getElementById(target)
      if (!element) return

      const enter = () => setHoveredSection(target)
      const leave = () =>
        setHoveredSection(curr => (curr === target ? null : curr))

      element.addEventListener("mouseenter", enter)
      element.addEventListener("mouseleave", leave)

      cleanups.push(() => {
        element.removeEventListener("mouseenter", enter)
        element.removeEventListener("mouseleave", leave)
      })
    })

    return () => {
      cleanups.forEach(fn => fn())
    }
  }, [sections])

  // methode zum scrollen beim klicken in der leiste
  const handleScroll = (targetId: string) => {
    onNavigate?.(targetId)
    const element = document.getElementById(targetId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <div className="mt-4 flex w-full items-center justify-between gap-2 overflow-x-auto px-2 sm:px-10">
      {sections.map(({ label, short, icon, target }) => {
        const isHover = hoveredSection === target
        const isComplete = Boolean(complete[target])

        return (
          <button
            type="button"
            key={target}
            aria-current={activeSection === target ? "step" : undefined}
            aria-label={`${label} ${isComplete ? "Complete" : "Not completed"}`}
            onClick={() => handleScroll(target)}
            onMouseEnter={() => setHoveredSection(target)}
            onMouseLeave={() =>
              setHoveredSection(curr => (curr === target ? null : curr))
            }
            title={label}
            className={`inline-flex shrink-0 items-center gap-2 h-9 px-2 lg:px-3 rounded-md cursor-pointer select-none
              transition-colors duration-200 ease-in-out
              ${
                isHover
                  ? "bg-green-100"
                  : "text-gray-700 hover:bg-green-50 hover:text-green-700 active:bg-green-200"
              }`}
          >
            {/* abschnitts-icon, bei abgeschlossenem abschnitt mit kleinem grünen haken */}
            <span className="relative shrink-0">
              {typeof icon === "string" ? <img src={icon} alt="" className="w-5 h-5 brightness-0" /> : <span className="flex h-5 w-5 items-center justify-center text-xl text-black" aria-hidden="true">{icon}</span>}
              {isComplete && (
                <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-600 text-white">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-2.5 w-2.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={4}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}
            </span>

            {/* text des jeweiligen abschnitts */}
            <span className="hidden lg:inline text-sm font-medium leading-none">
              {short}
            </span>
          </button>
        )
      })}
    </div>
  )
}
